/**
 * Workspace Audit Log
 *
 * Full workspace change history — who changed what and why.
 * Queries audit_events (item-level, workflow) and governance_events (decisions).
 * Satisfies enterprise compliance requirements for a full, filterable audit trail.
 *
 * Route: /prioritization/settings/audit-log
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FileText,
  Loader2,
  RefreshCw,
  Shield,
  ListChecks,
  Package,
  Layers,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  User,
  Search,
  Calendar,
  Download,
} from 'lucide-react';
import { formatDistanceToNow, format, subDays, startOfDay } from 'date-fns';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { getSafeSupabaseClient, getCurrentWorkspaceId } from '../../../lib/supabaseSync';

// ── Types ─────────────────────────────────────────────────────────────────────

type AuditSource = 'governance' | 'item';

interface UnifiedAuditRow {
  id: string;
  source: AuditSource;
  event_type: string;
  occurred_at: string;    // ISO string
  actor: string | null;   // actor_name or created_by
  payload: Record<string, unknown> | null;
  item_id?: string | null;
}

type CategoryFilter = 'all' | 'governance' | 'items' | 'sessions' | 'workflow';
type DateRange = '7d' | '30d' | '90d' | 'all';

// ── Category config ───────────────────────────────────────────────────────────

const CATEGORIES: Record<CategoryFilter, {
  label: string;
  icon: typeof Shield;
  color: string;
  bg: string;
}> = {
  all:        { label: 'All',        icon: FileText,   color: 'text-[var(--text-sec)]',   bg: 'bg-[var(--hover-tint)]'   },
  governance: { label: 'Governance', icon: Shield,     color: 'text-[var(--text-sec)]', bg: 'bg-[var(--slate-dim)]' },
  items:      { label: 'Items',      icon: Package,    color: 'text-[var(--text-sec)]',   bg: 'bg-[var(--slate-dim)]'   },
  sessions:   { label: 'Sessions',   icon: Layers,     color: 'text-[var(--text-sec)]', bg: 'bg-[var(--slate-dim)]' },
  workflow:   { label: 'Workflow',   icon: ListChecks, color: 'text-[var(--green)]',  bg: 'bg-[var(--green-dim)]'  },
};

const GOVERNANCE_SESSION_TYPES = [
  'SESSION_CREATED', 'SESSION_STARTED', 'SESSION_COMPLETED', 'SESSION_PUBLISHED',
  'CYCLE_CREATED', 'CYCLE_STARTED', 'CYCLE_COMPLETED', 'CYCLE_PHASE_CHANGED',
  'PRIORITY_SET_PUBLISHED', 'PRIORITY_SET_RATIFIED', 'PRIORITY_SET_DECIDED',
  'LOOP_ACCEPTED', 'LOOP_FINAL_REJECTED', 'PROPOSAL_ACCEPTED', 'REVISION_REQUESTED',
  'OVERRIDE_APPLIED', 'RANK_OVERRIDE',
];

const WORKFLOW_EVENT_TYPES = [
  'WorkflowStarted', 'WorkflowStepCompleted', 'WorkflowCompleted', 'WorkflowAbandoned',
  'EnvironmentReady', 'FirstIdeaSubmitted', 'FirstItemShaped', 'FirstCycleCreated',
  'FirstToolRun', 'FirstPriorityCommitted',
];

function categorize(row: UnifiedAuditRow): CategoryFilter {
  if (row.source === 'item') {
    if (WORKFLOW_EVENT_TYPES.includes(row.event_type)) return 'workflow';
    return 'items';
  }
  if (GOVERNANCE_SESSION_TYPES.includes(row.event_type)) return 'sessions';
  return 'governance';
}

function eventLabel(eventType: string): string {
  return eventType
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase());
}

// ── Row component ─────────────────────────────────────────────────────────────

function AuditRow({ row }: { row: UnifiedAuditRow }) {
  const [expanded, setExpanded] = useState(false);
  const cat = CATEGORIES[categorize(row)];
  const Icon = cat.icon;
  const hasDetail = !!row.payload && Object.keys(row.payload).length > 0;

  return (
    <div className="border-b border-[var(--rule)] dark:border-[var(--rule)] last:border-0">
      <button
        type="button"
        onClick={() => hasDetail && setExpanded(v => !v)}
        className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors ${hasDetail ? 'hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] cursor-pointer' : 'cursor-default'}`}
      >
        <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center mt-0.5 ${cat.bg}`}>
          <Icon className={`h-3.5 w-3.5 ${cat.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {eventLabel(row.event_type)}
            </span>
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wider ${cat.bg} ${cat.color}`}>
              {cat.label}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-[11px] text-[var(--text-ter)] dark:text-[var(--text-dim)]">
            {row.actor && (
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {row.actor}
              </span>
            )}
            <span className="flex items-center gap-1" title={format(new Date(row.occurred_at), 'PPpp')}>
              <Clock className="h-3 w-3" />
              {formatDistanceToNow(new Date(row.occurred_at), { addSuffix: true })}
            </span>
          </div>
        </div>
        {hasDetail && (
          expanded
            ? <ChevronDown className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0 mt-1" />
            : <ChevronRight className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0 mt-1" />
        )}
      </button>

      {expanded && row.payload && (
        <div className="px-4 pb-3 pl-14">
          <pre className="text-[11px] bg-[var(--surface)] dark:bg-[var(--surface-raised)] border border-[var(--rule)] dark:border-[var(--rule)] p-3 overflow-x-auto text-[var(--text-sec)] dark:text-[var(--text-sec)] max-h-48">
            {JSON.stringify(row.payload, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function WorkspaceAuditLog() {
  const [rows, setRows] = useState<UnifiedAuditRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<CategoryFilter>('all');
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const [search, setSearch] = useState('');

  const workspaceId = getCurrentWorkspaceId();

  const fetchAll = useCallback(async () => {
    if (!workspaceId) return;
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    setIsLoading(true);
    setError(null);

    const since = dateRange !== 'all'
      ? startOfDay(subDays(new Date(), dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90)).toISOString()
      : null;

    const [govResult, itemResult] = await Promise.all([
      // Governance events
      (() => {
        let q = supabase
          .from('governance_events')
          .select('id, event_type, occurred_at, actor_name, actor_id')
          .eq('workspace_id', workspaceId)
          .order('occurred_at', { ascending: false })
          .limit(300);
        if (since) q = q.gte('occurred_at', since);
        return q;
      })(),
      // Audit events (item-level + workflow)
      (() => {
        let q = supabase
          .from('audit_events')
          .select('id, event_type, created_at, created_by, payload, item_id')
          .eq('workspace_id', workspaceId)
          .order('created_at', { ascending: false })
          .limit(300);
        if (since) q = q.gte('created_at', since);
        return q;
      })(),
    ]);

    if (govResult.error) { setError(govResult.error.message); setIsLoading(false); return; }
    if (itemResult.error) { setError(itemResult.error.message); setIsLoading(false); return; }

    const govRows: UnifiedAuditRow[] = (govResult.data ?? []).map((e: Record<string, unknown>) => ({
      id: `gov-${e.id}`,
      source: 'governance' as const,
      event_type: e.event_type as string,
      occurred_at: e.occurred_at as string,
      actor: (e.actor_name as string | null) ?? (e.actor_id as string | null),
      payload: null,
    }));

    const itemRows: UnifiedAuditRow[] = (itemResult.data ?? []).map((e: Record<string, unknown>) => ({
      id: `audit-${e.id}`,
      source: 'item' as const,
      event_type: e.event_type as string,
      occurred_at: e.created_at as string,
      actor: e.created_by as string | null,
      payload: (e.payload as Record<string, unknown> | null) ?? null,
      item_id: e.item_id as string | null,
    }));

    const combined = [...govRows, ...itemRows]
      .sort((a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime());

    setRows(combined);
    setIsLoading(false);
  }, [workspaceId, dateRange]);

  useEffect(() => { void fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    let result = rows;
    if (category !== 'all') result = result.filter(r => categorize(r) === category);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(r =>
        r.event_type.toLowerCase().includes(q) ||
        r.actor?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [rows, category, search]);

  function downloadCSV() {
    const header = 'ID,Source,Event Type,Occurred At,Actor\n';
    const csv = header + filtered.map(r =>
      [r.id, r.source, r.event_type, r.occurred_at, r.actor ?? ''].join(',')
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `workspace-audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<FileText className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Audit Log"
        subtitle="Full workspace change history — governance decisions, item changes, and workflow events"
        actions={
          <div className="flex gap-2">
            <button
              onClick={downloadCSV}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] border border-[var(--rule)] dark:border-[var(--rule-bright)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              Export CSV
            </button>
            <button
              onClick={() => void fetchAll()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] border border-[var(--rule)] dark:border-[var(--rule-bright)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        }
      />

      {/* Summary stats */}
      <div className="grid grid-cols-4 gap-4">
        {Object.entries(CATEGORIES).filter(([k]) => k !== 'all').map(([key, cat]) => {
          const count = rows.filter(r => categorize(r) === key).length;
          return (
            <button
              key={key}
              onClick={() => setCategory(category === key ? 'all' : key as CategoryFilter)}
              className={`bg-[var(--bg-raised)] dark:bg-[var(--surface)] border  p-4 text-left transition-all ${
                category === key
                  ? 'border-[var(--slate)] dark:border-[var(--slate)] ring-2 ring-indigo-100 dark:ring-[var(--active-tint)]'
                  : 'border-[var(--rule)] dark:border-[var(--rule)] hover:border-[var(--rule)] dark:hover:border-[var(--rule-bright)]'
              }`}
            >
              <cat.icon className={`h-4 w-4 ${cat.color} mb-2`} />
              <div className="text-xl font-bold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{count}</div>
              <div className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-dim)]">{cat.label}</div>
            </button>
          );
        })}
      </div>

      {/* Filter bar */}
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] px-4 py-3 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-0 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)] pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search events or actors…"
            className="w-full pl-8 pr-3 py-1.5 text-sm bg-[var(--surface)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule-bright)] focus:outline-none dark:text-[var(--text-pri)]"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5 text-[var(--text-ter)]" />
          <select
            value={dateRange}
            onChange={e => setDateRange(e.target.value as DateRange)}
            className="text-sm bg-[var(--surface)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule-bright)] px-2 py-1.5 focus:outline-none dark:text-[var(--text-pri)]"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>
        </div>
        <span className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-dim)] ml-auto">
          {filtered.length} of {rows.length} events
        </span>
      </div>

      {/* Event list */}
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface)] border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-[var(--text-ter)]">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading audit log…</span>
          </div>
        ) : error ? (
          <div className="flex items-center gap-3 px-6 py-8 text-[var(--coral)]">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-[var(--text-ter)]">
            <FileText className="h-8 w-8 mb-3 opacity-30" />
            <p className="text-sm font-medium">No events found</p>
            <p className="text-[12px] mt-1">Try widening the date range or clearing filters.</p>
          </div>
        ) : (
          <>
            <div className="px-4 py-2.5 border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)]/60 dark:bg-[var(--surface-raised)]">
              <span className="text-[11px] font-semibold text-[var(--text-ter)] dark:text-[var(--text-dim)] uppercase tracking-wider">
                {filtered.length} event{filtered.length !== 1 ? 's' : ''}
              </span>
            </div>
            {filtered.map(row => <AuditRow key={row.id} row={row} />)}
          </>
        )}
      </div>
    </div>
  );
}
